import React from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageCircle, Sparkles, ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import { useI18n } from "../lib/i18n";
import { getPageWaLink } from "../data/firm";

/**
 * A persistent "engagement strip" mounted on every public page right
 * above the Footer. Provides two firm-priority CTAs (subscription
 * upsell + WhatsApp booking) so that visitors landing on any page
 * always see how to convert. Hidden inside the authenticated admin
 * and portal areas where it would be noise.
 */
export default function SubscriptionBar() {
  const { lang } = useI18n();
  const { pathname } = useLocation();
  const isAr = lang === "ar";

  // Hide in admin / portal / login / register / reset flows where the
  // bar would distract from the operator task at hand.
  if (
    pathname.startsWith("/admin") ||
    pathname.startsWith("/portal") ||
    pathname.startsWith("/login") ||
    pathname.startsWith("/register") ||
    pathname.startsWith("/forgot-password") ||
    pathname.startsWith("/reset-password") ||
    pathname.startsWith("/subscriptions")
  ) {
    return null;
  }

  const waLink = getPageWaLink("consultation", lang);
  const Arrow = isAr ? ArrowLeft : ArrowRight;

  return (
    <section
      data-testid="subscription-bar"
      className="relative overflow-hidden border-y border-slate-200"
      style={{ background: "var(--ink, #0b1a2d)" }}
    >
      {/* Decorative gold ribbon */}
      <span
        aria-hidden
        className="absolute inset-y-0 start-0 w-1.5"
        style={{ background: "var(--gold-deep)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Left: subscription pitch */}
          <div className="lg:col-span-7">
            <p
              className="eyebrow inline-flex items-center gap-2"
              style={{ color: "var(--gold-deep)" }}
            >
              <Sparkles className="w-3.5 h-3.5" />
              {isAr ? "اشتراكات الخدمات القانونية" : "Legal subscriptions"}
            </p>
            <h2
              className="font-display text-3xl lg:text-4xl mt-3 leading-[1.05] tracking-tight text-white"
              dir={isAr ? "rtl" : "ltr"}
            >
              {isAr
                ? "محامٍ متفرغ لمؤسستك أو لك شخصياً، باشتراك شهري."
                : "Get a dedicated lawyer on retainer for you or your business."}
            </h2>
            <p
              className="mt-3 text-sm lg:text-base leading-relaxed text-white/70 max-w-2xl"
              dir={isAr ? "rtl" : "ltr"}
            >
              {isAr
                ? "اشتراكات شهرية للأفراد والشركات تشمل محادثة غير محدودة مع سامي، ومراجعة المستندات، وصياغة العقود، وخطاً مباشراً مع محامي المكتب."
                : "Monthly plans for individuals and businesses: unlimited chat with Sami, document reviews, contract drafting, and a direct line to the firm's lawyers."}
            </p>
          </div>

          {/* Right: dual CTAs */}
          <div className="lg:col-span-5 flex flex-col gap-3 lg:items-end">
            <Link
              to="/subscriptions"
              data-testid="subbar-subscriptions-link"
              className="block w-full lg:w-auto"
            >
              <Button className="btn-gold rounded-sm w-full lg:w-auto h-12 px-7 text-[15px]">
                <Sparkles className="w-4 h-4 me-2" />
                {isAr ? "تصفّح خطط الاشتراك" : "Explore subscription plans"}
                <Arrow className="w-4 h-4 ms-2" />
              </Button>
            </Link>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="subbar-whatsapp-link"
              className="block w-full lg:w-auto"
            >
              <Button
                variant="outline"
                className="rounded-sm w-full lg:w-auto h-12 px-7 text-[15px] border-white/30 text-white hover:bg-white/10 hover:text-white"
              >
                <MessageCircle className="w-4 h-4 me-2" />
                {isAr
                  ? "أو احجز استشارة عبر واتساب"
                  : "Or book via WhatsApp"}
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
